import { useEffect, useState } from "react";
import API from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { TenantAdminFields, EMPTY_ADMIN } from "./TenantAdminFields";
import { Crown, PaperPlaneTilt, ArrowsClockwise, UserPlus, Copy, Check, Key } from "@phosphor-icons/react";
import { SetPasswordDialog } from "./SetPasswordDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "");

// Platform → Tenants editor: who runs this workspace. Resend / regenerate pending invites, add another admin, transfer ownership.
export function TenantAdminsPanel({ tenant }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [adding, setAdding] = useState(false);
  const [adminForm, setAdminForm] = useState(EMPTY_ADMIN);
  const [makeOwner, setMakeOwner] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [demote, setDemote] = useState(false);
  const [pwFor, setPwFor] = useState(null);

  const load = () => API.get(`/tenants/${tenant.id}/admins`).then(({ data }) => setData(data)).catch(() => setData({ admins: [] }));
  useEffect(() => { if (tenant?.id) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tenant?.id]);

  const resend = async (a, regenerate) => {
    setBusy(`${regenerate ? "regen" : "resend"}-${a.user_id}`);
    try {
      const { data } = await API.post(`/tenants/${tenant.id}/admins/${a.user_id}/resend-invite`, { regenerate });
      setLastLink(data);
      if (data.email_sent === false) toast.warning(`E-mail to ${a.email} couldn't be sent — copy the link below and send it yourself`);
      else toast.success(data.regenerated ? `New invite link issued and e-mailed to ${a.email}` : `Invite re-sent to ${a.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend the invite"); }
    finally { setBusy(""); }
  };

  const addAdmin = async () => {
    if (!adminForm.email.trim()) return toast.error("Admin email is required");
    if (adminForm.mode === "password" && adminForm.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy("add");
    try {
      const { data } = await API.post(`/tenants/${tenant.id}/admins`, { ...adminForm, make_owner: makeOwner && adminForm.role === "super_admin" });
      if (data.email_sent === false) toast.warning(`${data.role_label} ${data.email} added, but the e-mail couldn't be sent${data.invite_url ? " — copy the link below and send it yourself" : ""}`);
      else toast.success(`${data.role_label} ${data.email} added${data.mode === "invite" ? " — invite e-mailed" : " — welcome e-mail sent"}`);
      if (data.invite_url) setLastLink(data);
      setAdding(false); setAdminForm(EMPTY_ADMIN); setMakeOwner(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add the admin"); }
    finally { setBusy(""); }
  };

  const transfer = async () => {
    const target = data?.admins.find((a) => a.user_id === transferTo);
    if (!target) return toast.error("Pick the new owner");
    if (!window.confirm(`Make ${target.name || target.email} the owner of ${tenant.name}?${demote ? " The current owner becomes an Operations Director." : ""}`)) return;
    setBusy("transfer");
    try {
      await API.post(`/tenants/${tenant.id}/transfer-ownership`, { user_id: transferTo, demote_previous: demote });
      toast.success(`${target.name || target.email} now owns ${tenant.name}`);
      setTransferTo(""); setDemote(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't transfer ownership"); }
    finally { setBusy(""); }
  };

  const copy = async (url) => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {} };

  if (!data) return <p className="text-xs text-muted-foreground">Loading admins…</p>;
  const candidates = data.admins.filter((a) => !a.is_owner && a.invite_status !== "pending");
  return (
    <div className="space-y-3 min-w-0" data-testid="tenant-admins-panel">
      <div className="flex items-center justify-between">
        <Label>Workspace admins</Label>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAdding((v) => !v)} data-testid="tenant-admins-add-toggle"><UserPlus size={13} className="mr-1" /> {adding ? "Cancel" : "Add admin"}</Button>
      </div>
      <div className="space-y-1.5">
        {data.admins.length === 0 && <p className="text-xs text-muted-foreground" data-testid="tenant-admins-empty">No admins yet — add one below.</p>}
        {data.admins.map((a) => (
          <div key={a.user_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-2.5 py-1.5 text-xs min-w-0" data-testid={`tenant-admin-row-${a.user_id}`}>
            <div className="min-w-0 flex-1 basis-40">
              <p className="font-medium truncate flex items-center gap-1.5">{a.name || a.email}{a.is_owner && <Badge variant="outline" className="text-[9px] gap-1 py-0 h-4" data-testid={`tenant-admin-owner-${a.user_id}`}><Crown size={9} weight="fill" /> Owner</Badge>}</p>
              <p className="text-muted-foreground truncate">{a.email} · {a.role_label}{a.invite_status === "pending" ? (a.invite_expired ? ` · invite expired ${fmt(a.invite_expires_at)}` : ` · invite pending${a.invite_expires_at ? `, expires ${fmt(a.invite_expires_at)}` : ""}`) : ""}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {a.invite_status === "pending" && !a.invite_expired && <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-[10px]" disabled={busy === `resend-${a.user_id}`} onClick={() => resend(a, false)} data-testid={`tenant-admin-resend-${a.user_id}`}><PaperPlaneTilt size={11} className="mr-1" /> Resend</Button>}
              {a.invite_status === "pending" && <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-[10px]" disabled={busy === `regen-${a.user_id}`} onClick={() => resend(a, true)} data-testid={`tenant-admin-regenerate-${a.user_id}`}><ArrowsClockwise size={11} className="mr-1" /> New link</Button>}
              <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-[10px]" onClick={() => setPwFor(a)} data-testid={`tenant-admin-set-password-${a.user_id}`}><Key size={11} className="mr-1" /> Set password</Button>
            </div>
          </div>
        ))}
      </div>
      <SetPasswordDialog open={!!pwFor} onClose={() => setPwFor(null)} person={pwFor} endpoint={pwFor ? `/tenants/${tenant.id}/admins/${pwFor.user_id}/set-password` : ""} onDone={load} />
      {lastLink?.invite_url && (
        <div className="rounded-md bg-muted/40 p-2 text-[11px] flex items-start gap-2 min-w-0" data-testid="tenant-admins-last-link">
          <span className="font-mono break-all min-w-0 flex-1">{lastLink.invite_url}</span>
          <button type="button" onClick={() => copy(lastLink.invite_url)} className="shrink-0 text-muted-foreground hover:text-foreground" data-testid="tenant-admins-copy-link">{copied ? <Check size={12} /> : <Copy size={12} />}</button>
        </div>
      )}
      {adding && (
        <div className="rounded-md border border-dashed border-border p-3 space-y-3" data-testid="tenant-admins-add-form">
          <TenantAdminFields value={adminForm} onChange={setAdminForm} title="New admin" subtitle="They'll get full access to this workspace alongside the existing admins." />
          <label className={`flex items-center gap-2 text-xs ${adminForm.role !== "super_admin" ? "opacity-50" : ""}`}>
            <Checkbox checked={makeOwner && adminForm.role === "super_admin"} disabled={adminForm.role !== "super_admin"} onCheckedChange={(v) => setMakeOwner(!!v)} data-testid="tenant-admins-make-owner" /> Make them the workspace owner{adminForm.role !== "super_admin" ? " (owners are Super Admins)" : ""}
          </label>
          <Button type="button" size="sm" className="w-full" disabled={busy === "add"} onClick={addAdmin} data-testid="tenant-admins-add-submit">{busy === "add" ? "Adding…" : adminForm.mode === "invite" ? "Add & send invite" : "Add admin"}</Button>
        </div>
      )}
      {candidates.length > 0 && (
        <div className="rounded-md border border-border p-3 space-y-2" data-testid="tenant-admins-transfer">
          <Label className="text-xs flex items-center gap-1"><Crown size={12} /> Transfer ownership</Label>
          <Select value={transferTo} onValueChange={setTransferTo}>
            <SelectTrigger className="h-8 text-xs" data-testid="tenant-admins-transfer-select"><SelectValue placeholder="Choose the new owner…" /></SelectTrigger>
            <SelectContent>
              {candidates.map((a) => <SelectItem key={a.user_id} value={a.user_id} data-testid={`tenant-admins-transfer-option-${a.user_id}`}>{a.name || a.email} — {a.role_label}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={demote} onCheckedChange={(v) => setDemote(!!v)} data-testid="tenant-admins-demote-previous" /> Current owner becomes an Operations Director (otherwise stays Super Admin)
          </label>
          <Button type="button" size="sm" variant="outline" className="w-full" disabled={!transferTo || busy === "transfer"} onClick={transfer} data-testid="tenant-admins-transfer-submit">{busy === "transfer" ? "Transferring…" : "Transfer ownership"}</Button>
        </div>
      )}
    </div>
  );
}
